import React from 'react';
import PropTypes from 'prop-types';
import * as d3 from 'd3';
import withStyles from 'isomorphic-style-loader/withStyles';
import s from './HeightGraph.css';
import mathUtils from '../../core/mathUtils';
import AppContext from '../../AppContext';

const xPadding = 60;
const yPadding = 35;
const innerXDim = 550;
const innerYDim = 350;
const xDim = innerXDim + 2 * xPadding;
const yDim = innerYDim + 2 * yPadding;
const maxSigma = 6;
const numPts = 200;
const legendLeftStart = innerXDim / 2 - 130;
const legendGap = 170;

class HeightGraph extends React.Component {
  static propTypes = {
    alpha: PropTypes.number,
    selectivity: PropTypes.number,
    mean: PropTypes.number,
    sd: PropTypes.number,
  }

  static contextType = AppContext;

  componentDidMount() {
    this.promises = Promise.all([
      mathUtils.addLookupTable(this.context, '/posts/height/phi.json', 'phi'),
      mathUtils.addLookupTable(this.context, '/posts/height/inverse_phi.json', 'inverse_phi'),
    ]);
    this.promises.then(() => {
      this.drawGraph();
    });
  }

  componentWillReceiveProps() {
    if ('svg' in this) {
      this.drawGraph();
    }
  }

  shouldComponentUpdate() {
    return false;
  }

  phi(x) {
    if (x <= 0) {
      return Math.exp(mathUtils.lookupFunc(x, 'phi'));
    }
    return 1 - Math.exp(mathUtils.lookupFunc(-x, 'phi'));
  }

  inversePhi(x) {
    if (x <= 0.5) {
      return mathUtils.lookupFunc(Math.log(x), 'inverse_phi');
    }
    return -mathUtils.lookupFunc(Math.log(1 - x), 'inverse_phi');
  }

  drawGraph() {
    this.promises.then(() => {
      if (this.svg) {
        d3.select('#height-graph-svg').remove();
      }
      this.svg = d3.select('#height-graph')
        .append('svg')
        .attr('id', 'height-graph-svg')
        .attr('width', xDim)
        .attr('height', yDim)
        .append('g')
        .attr('transform', `translate(${xPadding},${yPadding})`)
        .attr('width', innerXDim)
        .attr('height', innerYDim);

      const { mean, sd, alpha, selectivity } = this.props;

      const xmin = mean - maxSigma * sd;
      const xmax = mean + maxSigma * sd;

      const vari = sd * sd;
      const contraAlpha = Math.sqrt(1 - alpha * alpha);
      const baseZScore = this.inversePhi(1 - 1 / selectivity);

      const data = [];
      for (let i = 0; i < numPts; i++) {
        const x = xmin + (xmax - xmin) * i / (numPts - 1);

        const dx = x - mean;
        const generalY = Math.sqrt(1 / (2 * vari * Math.PI)) * Math.exp(-dx * dx / (2 * vari));

        const cdfTerm = (1 - this.phi(
          (baseZScore - alpha * dx / sd) / contraAlpha,
        ));
        const specificY = cdfTerm * selectivity * generalY;

        data.push({
          x,
          generalY,
          specificY,
        });
      }

      const xScale = d3.scaleLinear()
        .domain([xmin, xmax])
        .range([0, innerXDim]);
      const yScale = d3.scaleLinear()
        .domain([0, Math.max(...data.map((x) => Math.max(x.generalY, x.specificY))) * 1.02])
        .range([innerYDim, 0]);

      const xAxis = d3.axisBottom()
        .scale(xScale);
      const yAxis = d3.axisLeft()
        .scale(yScale);

      const generalArea = d3.area()
        .x((d) => xScale(d.x))
        .y0(() => yScale(0))
        .y1((d) => yScale(d.generalY));
      const specificArea = d3.area()
        .x((d) => xScale(d.x))
        .y0(() => yScale(0))
        .y1((d) => yScale(d.specificY));

      this.svg
        .append('path')
        .datum(data)
        .attr('class', s.general)
        .attr('d', generalArea);
      this.svg
        .append('path')
        .datum(data)
        .attr('class', s.specific)
        .attr('d', specificArea);

      this.svg
        .append('g')
        .attr('transform', `translate(0, ${yScale(0)})`)
        .call(xAxis);
      this.svg
        .append('g')
        .call(yAxis);

      this.svg
        .append('text')
        .attr('x', innerXDim / 2 - 55)
        .attr('y', innerYDim + 30)
        .text('height / cm');
      this.svg
        .append('text')
        .attr('x', -innerYDim / 2 - 30)
        .attr('y', -40)
        .attr('transform', 'rotate(-90)')
        .text('frequency');

      // legend
      this.svg
        .append('rect')
        .attr('class', s.general)
        .attr('x', legendLeftStart)
        .attr('y', -20)
        .attr('width', 12)
        .attr('height', 12);
      this.svg
        .append('text')
        .attr('x', legendLeftStart + 18)
        .attr('y', -11)
        .attr('class', s.legend)
        .text('general population');
      this.svg
        .append('rect')
        .attr('class', s.specific)
        .attr('x', legendLeftStart + legendGap)
        .attr('y', -20)
        .attr('width', 12)
        .attr('height', 12);
      this.svg
        .append('text')
        .attr('x', legendLeftStart + legendGap + 18)
        .attr('y', -11)
        .attr('class', s.legend)
        .text('professionals');
    });
  }

  render() {
    return (
      <div id="height-graph" className={s.container} />
    );
  }
}

export default withStyles(s)(HeightGraph);



// WEBPACK FOOTER //
// src/components/HeightGraph/HeightGraph.js